<template>
  <VDialog
    v-model="showDialog"
    activator="parent"
    max-width="600px"
    :transition="false"
    :z-index="9999"
    attach="#app"
  >
    <template #default>
      <VCard class="p-4">
        <VCardTitle class="flex justify-space-between items-center p-0">
          <div class="text-h4 p-4 pb-2">{{ t('Sort patients') }}</div>
          <VBtn
            icon="close"
            variant="plain"
            class="button-small mr-2"
            size="small"
            :ripple="false"
            data-testid="btn-close"
            @click="closeDialog"
          />
        </VCardTitle>
        <div class="overflow-y-auto">
          <VCardItem class="p-2">
            <VSelect
              v-model="sortBy.sortDirection"
              :items="[
                { title: t('Ascending'), value: 'ASCENDING' },
                { title: t('Descending'), value: 'DESCENDING' }
              ]"
              :label="t('Sort Direction')"
              class="my-4"
              hide-details
              base-color="secondary-text"
            />

            <VRadioGroup v-model="sortBy.sortBy" class="mb-4" hide-details>
              <VRadio
                :label="t('Patient Name')"
                :value="AllCaseOrderOptionsDTO.PATIENT_NAME"
                :ripple="false"
                base-color="secondary-text"
              />
              <VRadio
                :label="getParameterLabel(Parameter.HeartRate)"
                :value="AllCaseOrderOptionsDTO.HEART_RATE"
                :ripple="false"
                base-color="secondary-text"
              />
              <VRadio
                :label="getParameterLabel(Parameter.OxygenSaturation)"
                :value="AllCaseOrderOptionsDTO.SPO2"
                :ripple="false"
                base-color="secondary-text"
              />
              <VRadio
                :label="getParameterLabel(Parameter.RespiratoryRate)"
                :value="AllCaseOrderOptionsDTO.RR"
                :ripple="false"
                base-color="secondary-text"
              />
              <VRadio
                :label="t('Body Temperature')"
                :value="AllCaseOrderOptionsDTO.TEMP"
                :ripple="false"
                base-color="secondary-text"
              />
              <VRadio
                :label="t('Organ Support')"
                :value="AllCaseOrderOptionsDTO.ORGAN_SUPPORT"
                :ripple="false"
                base-color="secondary-text"
              />
            </VRadioGroup>
          </VCardItem>
        </div>

        <template #actions>
          <VCardActions>
            <div class="pt-2">
              <VBtn
                variant="text"
                color="primary"
                data-testid="btn-cancel"
                class="ml-auto button-large"
                size="large"
                :text="t('Cancel')"
                @click="closeDialog"
              />
              <VBtn
                variant="flat"
                color="primary"
                class="ml-4 button-large"
                size="large"
                :text="t('Apply')"
                data-testid="btn-confirm"
                @click="onSubmit"
              />
            </div>
          </VCardActions>
        </template>
      </VCard>
    </template>
  </VDialog>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import {
  usePatientsWithVitalSignsStore,
  type VitalSignsSortBy
} from '@/stores/patientsWithVitalSigns'
import { AllCaseOrderOptionsDTO } from '@/generated'
import useParameterLabel from '@/composables/useParameterLabel'
import { Parameter } from '@/enums/Parameter'

const { t } = useI18n()
const { getParameterLabel } = useParameterLabel()
const showDialog = ref<boolean>(false)

const patientsWithVitalSignsStore = usePatientsWithVitalSignsStore()
const { commitVitalSigns } = patientsWithVitalSignsStore
const { vitalSignsSortBy } = storeToRefs(patientsWithVitalSignsStore)

const sortBy = ref<VitalSignsSortBy>({ ...vitalSignsSortBy.value })

watch(showDialog, (isOpening) => {
  if (isOpening) {
    sortBy.value = { ...vitalSignsSortBy.value }
  }
})

function onSubmit() {
  vitalSignsSortBy.value = sortBy.value

  commitVitalSigns()
  closeDialog()
}

function closeDialog() {
  showDialog.value = false
}
</script>
